import React, { useCallback, useRef } from "react";
import { Typography } from "@mzc-pdc/ui";
import Tooltip from "@components_v2/Tooltip/tooltip";
import useTextOverflow from "@hooks/useTextOverflow";

/**
 * 말줄임(…)이 실제로 발생했을 때만 툴팁으로 전체 텍스트를 노출하는 Typography.
 *
 * Typography 의 props 를 그대로 전달하며 MUI Tooltip 은 DOM 노드를 추가하지 않으므로
 * 기존 마크업·스타일을 그대로 유지한 채 툴팁만 덧붙는다. (DAM-306)
 *
 * @param {React.ReactNode} tooltipTitle 툴팁에 표시할 내용. 없으면 children 을 사용한다.
 * @param {number} lineClamp 2 이상이면 해당 줄 수까지 줄바꿈한 뒤 말줄임 처리한다.
 */
const EllipsisTypography = React.forwardRef(
    ({ children, tooltipTitle, tooltipPlacement = "bottom", lineClamp, sx, ...rest }, forwardedRef) => {
        const elementRef = useRef(null);
        const isOverflow = useTextOverflow(elementRef, [children, lineClamp]);

        const handleRef = useCallback(
            (node) => {
                elementRef.current = node;
                if (typeof forwardedRef === "function") forwardedRef(node);
                else if (forwardedRef) forwardedRef.current = node;
            },
            [forwardedRef],
        );

        const clampSx =
            lineClamp > 1
                ? {
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: lineClamp,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflow: "hidden",
                  }
                : null;

        return (
            <Tooltip title={isOverflow ? tooltipTitle ?? children : ""} placement={tooltipPlacement}>
                <Typography ref={handleRef} sx={{ ...clampSx, ...sx }} {...rest}>
                    {children}
                </Typography>
            </Tooltip>
        );
    },
);

EllipsisTypography.displayName = "EllipsisTypography";

export default EllipsisTypography;
